import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNewArrivals } from '../../hooks/useNewArrivals';
import { useDragScroll } from '../../hooks/useDragScroll';
import { ProductCard } from '../ui/ProductCard';

const CAROUSEL_ITEM_WIDTH_PX = 240;

function NewArrivalsSkeleton() {
  return (
    <div className="flex gap-4 overflow-hidden">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div key={idx} className="w-44 sm:w-56 shrink-0">
          <div className="aspect-square rounded-2xl bg-champagne animate-pulse" />
          <div className="h-3 w-3/4 bg-champagne rounded mt-3 animate-pulse" />
          <div className="h-3 w-1/2 bg-champagne rounded mt-2 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

export function NewArrivals() {
  const { items, isLoading, error, isEmpty } = useNewArrivals(true);
  const {
    ref: scrollRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onMouseEnter,
    onTouchStart,
    onTouchEnd,
    onClickCapture,
  } = useDragScroll<HTMLDivElement>();
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [items, scrollRef]);

  const handleScrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -CAROUSEL_ITEM_WIDTH_PX * 2, behavior: 'smooth' });
  };

  const handleScrollNext = () => {
    scrollRef.current?.scrollBy({ left: CAROUSEL_ITEM_WIDTH_PX * 2, behavior: 'smooth' });
  };

  if (error || isEmpty) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 pt-10">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-chocolate-dark mb-1">
            Novidades
          </p>
          <h2 className="text-xl md:text-2xl font-display font-semibold text-text-dark">Chegou agora na loja</h2>
          <div className="w-9 h-0.5 bg-chocolate mt-3" />
        </div>
        <Link
          to="/catalogo?sort=5"
          className="text-sm font-semibold text-chocolate hover:text-chocolate-dark transition-colors whitespace-nowrap"
        >
          Ver tudo →
        </Link>
      </div>

      {isLoading ? (
        <NewArrivalsSkeleton />
      ) : (
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto no-scrollbar pb-2 pr-4 cursor-grab select-none [scroll-behavior:auto]"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onClickCapture={onClickCapture}
          >
            {items.map((item) => (
              <div key={item.id} className="w-44 sm:w-56 shrink-0">
                <ProductCard product={item} />
              </div>
            ))}
          </div>
          {hasOverflow && (
            <>
              <button
                type="button"
                onClick={handleScrollPrev}
                aria-label="Anterior"
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleScrollNext}
                aria-label="Próximo"
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
